import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import updateCommunityAppService from '../../services/communityApps/updateCommunityAppService';
import communityAppService from '../../services/communityApps/communityAppService';
import CommunityAppDetail from './CommunityAppDetail';
import { COMMUNITY_DETAIL_TABS } from '../../constants';
import { mapCommunityApp } from '../../helpers/mapCommunityApp';

const EditCommunityApp = ({
  open,
  dispatchUpdateCommunityAppPut,
  updateCommunityAppFetch,
  dispatchCommunityAppGet,
  communityAppFetch,
  returnRoute,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, actionId } = useParams();

  const rights = useSelector(GetRights);

  const readOnly = useMemo(() => !hasRight(rights, 'COMMUNITYAPPS.MANAGE.ALL'), [rights]);

  const tag = useSelector(state => get(state, 'repository.community.value.tag'));
  const appInstallationTag = useSelector(state =>
    get(state, 'repository.appInstallation.value.tag')
  );

  const handleSubmit = useCallback(
    body =>
      dispatchUpdateCommunityAppPut(
        actionId,
        mapCommunityApp(body, appInstallationTag, tag),
        () => navigate(returnRoute || `/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.APPS}`),
        t
      ),
    [returnRoute, actionId, id, appInstallationTag, tag, dispatchUpdateCommunityAppPut, t, navigate]
  );

  useEffect(() => {
    if (open && id && actionId) {
      dispatchCommunityAppGet(actionId, undefined, t);
    }
  }, [id, open, actionId, t, dispatchCommunityAppGet]);

  if (!open) return null;

  return (
    <CommunityAppDetail
      header={communityAppFetch.value?.alias || communityAppFetch.value?.appInstallation?.name}
      onSubmit={handleSubmit}
      submitProcessing={updateCommunityAppFetch.pending}
      returnRoute={returnRoute || `/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.APPS}`}
      cancelRoute={returnRoute || `/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.APPS}`}
      dataLoading={communityAppFetch.pending}
      data={
        !communityAppFetch.pending && {
          ...(communityAppFetch.value || {}),
          communityAppFeatures: (communityAppFetch.value?.communityAppFeatures || []).map(f => ({
            ...f,
            communityAppFeatureRoles: (f.communityAppFeatureRoles || []).map(
              r => r.appInstallationFeatureRole?.tag
            ),
          })),
          community: { tag },
        }
      }
      showRemoveButton
      editMode
      readOnly={readOnly}
    />
  );
};

EditCommunityApp.propTypes = {
  dispatchUpdateCommunityAppPut: PropTypes.func.isRequired,
  updateCommunityAppFetch: PropTypes.object.isRequired,
  dispatchCommunityAppGet: PropTypes.func.isRequired,
  communityAppFetch: PropTypes.object.isRequired,
  returnRoute: PropTypes.string,
  open: PropTypes.bool,
};

EditCommunityApp.defaultProps = {
  open: false,
  returnRoute: undefined,
};

export default compose(communityAppService, updateCommunityAppService)(EditCommunityApp);
